import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, PawPrint, Trash2, Scissors, Sparkles, RotateCcw, Link2, Loader2, Heart, GitCompare, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import { listDogs, deleteDog, updateHistoryEntry, toggleFavorite, sortHistory } from "@/lib/dogProfiles";
import { getStyleById } from "@/lib/groomingStyles";
import { createShareLink, copyOrShare } from "@/lib/share";
import CompareModal from "@/components/CompareModal";

function fmtDate(ts) {
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function MyDogs() {
  const navigate = useNavigate();
  const [dogs, setDogs] = useState([]);
  const [sharing, setSharing] = useState(null);
  const [compareDog, setCompareDog] = useState(null);
  const [editing, setEditing] = useState(null); // { entryId, value }

  useEffect(() => {
    setDogs(listDogs());
  }, []);

  const removeDog = (id) => {
    deleteDog(id);
    setDogs(listDogs());
  };

  const startRename = (h) => setEditing({ entryId: h.id, value: h.label || "" });
  const cancelRename = () => setEditing(null);
  const saveRename = (dogId) => {
    if (!editing) return;
    const label = editing.value.trim().slice(0, 40);
    updateHistoryEntry(dogId, editing.entryId, { label });
    setDogs(listDogs());
    setEditing(null);
    toast.success(label ? "Look renamed" : "Custom name cleared");
  };

  const onToggleFavorite = (dogId, entryId) => {
    const entry = toggleFavorite(dogId, entryId);
    setDogs(listDogs());
    if (entry) {
      toast.success(entry.favorite ? "Added to favourites" : "Removed from favourites");
    }
  };

  const shareEntry = async (dog, h) => {
    let url = h.shareUrl;
    if (!url) {
      if (!dog.originalPhoto || !h.previewImage) {
        toast.error("This entry has no saved photos to share.");
        return;
      }
      setSharing(h.id);
      try {
        const style = getStyleById(h.styleId) || { id: h.styleId, name: h.styleName };
        ({ url } = await createShareLink({
          beforeImage: dog.originalPhoto,
          afterImage: h.previewImage,
          style,
          dogName: dog.name,
          breed: dog.breed,
          age: dog.age,
          bodyLength: h.bodyLength,
          faceStyle: h.faceStyle,
          earStyle: h.earStyle,
          tailStyle: h.tailStyle,
          customInstructions: h.customInstructions,
        }));
        updateHistoryEntry(dog.id, h.id, { shareUrl: url });
        setDogs(listDogs());
      } catch {
        toast.error("We couldn't create a link right now. Please try again.");
        return;
      } finally {
        setSharing(null);
      }
    }
    const result = await copyOrShare(url);
    if (result === "copied") toast.success("Link copied", { description: "Send it to your groomer." });
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]" data-testid="my-dogs-page">
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <span className="text-xs font-bold tracking-widest uppercase text-[#c84125]">
              My Dogs
            </span>
            <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
              Your pack & grooming history.
            </h1>
            <p className="mt-2 text-sm text-[#475569] max-w-xl">
              Save each dog's grooming preferences so you can request the same style again next visit — or try something new.
            </p>
          </div>
          <Link
            to="/studio"
            data-testid="my-dogs-new-preview"
            className="inline-flex items-center gap-2 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white font-semibold px-5 py-3 shadow-md shadow-[#ff6b4a]/25 transition active:scale-[0.98]"
          >
            New Preview <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {dogs.length === 0 && (
          <div
            data-testid="my-dogs-empty"
            className="mt-10 rounded-3xl border border-dashed border-[#eae4d9] bg-white p-10 text-center"
          >
            <div className="mx-auto w-14 h-14 rounded-2xl bg-[#f3efe6] flex items-center justify-center">
              <PawPrint className="w-6 h-6 text-[#0f172a]" />
            </div>
            <h3 className="mt-4 font-display text-xl font-bold text-[#0f172a]">
              No saved dogs yet.
            </h3>
            <p className="mt-2 text-sm text-[#64748b] max-w-md mx-auto">
              After your first preview, save it here to build your dog's grooming history.
            </p>
            <Link
              to="/studio"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white text-sm font-semibold px-5 py-3 transition"
              data-testid="my-dogs-empty-cta"
            >
              Create your first preview <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}

        <div className="mt-8 space-y-4">
          {dogs.map((dog) => (
            <article
              key={dog.id}
              data-testid={`dog-card-${dog.id}`}
              className="rounded-3xl border border-[#eae4d9] bg-white shadow-sm overflow-hidden"
            >
              <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-40 h-40 rounded-2xl overflow-hidden bg-[#f3efe6] shrink-0">
                  {dog.originalPhoto ? (
                    <img src={dog.originalPhoto} alt={dog.name || "Dog"} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#64748b]">
                      <PawPrint className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-display text-xl sm:text-2xl font-extrabold text-[#0f172a] tracking-tight truncate">
                        {dog.name || "Unnamed pup"}
                      </h3>
                      <p className="text-xs text-[#64748b] mt-0.5">
                        {[dog.breed || (dog.breedUnknown ? "Breed unsure" : "—"), dog.age].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <button
                      data-testid={`delete-dog-${dog.id}`}
                      onClick={() => removeDog(dog.id)}
                      className="w-9 h-9 rounded-full bg-white hover:bg-[#fff0ed] border border-[#eae4d9] flex items-center justify-center text-[#c84125] transition"
                      aria-label="Remove dog"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="mt-3 flex items-center gap-2 text-xs text-[#64748b]">
                    <Scissors className="w-3.5 h-3.5" />
                    <span>Grooming history · {dog.history?.length || 0} entries</span>
                  </div>

                  {dog.history?.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid={`history-list-${dog.id}`}>
                      {sortHistory(dog.history).slice(0, 6).map((h) => {
                        const style = getStyleById(h.styleId) || { name: h.styleName };
                        const details = [h.bodyLength && `Body ${h.bodyLength}`, h.faceStyle && `Face ${h.faceStyle}`, h.earStyle && `Ears ${h.earStyle}`, h.tailStyle && `Tail ${h.tailStyle}`]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <li
                            key={h.id}
                            data-testid={`history-entry-${h.id}`}
                            className={`rounded-2xl border bg-[#fdfbf7] p-3 transition ${
                              h.favorite ? "border-[#ff6b4a] ring-1 ring-[#ff6b4a]/40 bg-[#fff6f3]" : "border-[#eae4d9]"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#f3efe6] shrink-0">
                                {h.previewImage && (
                                  <img src={h.previewImage} alt={style?.name} className="w-full h-full object-cover" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                {editing?.entryId === h.id ? (
                                  <div className="flex items-center gap-1.5">
                                    <input
                                      autoFocus
                                      data-testid={`rename-input-${h.id}`}
                                      value={editing.value}
                                      maxLength={40}
                                      onChange={(e) => setEditing({ entryId: h.id, value: e.target.value })}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") saveRename(dog.id);
                                        if (e.key === "Escape") cancelRename();
                                      }}
                                      placeholder={style?.name || "Name this look"}
                                      className="flex-1 min-w-0 rounded-lg border border-[#ff6b4a] bg-white px-2.5 py-1.5 text-sm text-[#0f172a] outline-none focus:ring-2 focus:ring-[#ff6b4a]/25"
                                    />
                                    <button
                                      type="button"
                                      data-testid={`rename-save-${h.id}`}
                                      onClick={() => saveRename(dog.id)}
                                      aria-label="Save name"
                                      className="w-8 h-8 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white flex items-center justify-center shrink-0 transition"
                                    >
                                      <Check className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      data-testid={`rename-cancel-${h.id}`}
                                      onClick={cancelRename}
                                      aria-label="Cancel"
                                      className="w-8 h-8 rounded-full bg-white hover:bg-[#f3efe6] border border-[#eae4d9] text-[#64748b] flex items-center justify-center shrink-0 transition"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <div className="text-sm font-semibold text-[#0f172a] truncate" data-testid={`entry-name-${h.id}`}>
                                      {h.label || style?.name || "Custom"}
                                    </div>
                                    <div className="text-[11px] text-[#64748b] truncate">
                                      {h.label && style?.name ? `${style.name} · ` : ""}
                                      {fmtDate(h.createdAt)}
                                      {details ? ` · ${details}` : ""}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                            {editing?.entryId !== h.id && (
                            <div className="mt-2.5 flex items-center justify-end gap-1.5 flex-wrap">
                              <button
                                type="button"
                                data-testid={`rename-entry-${h.id}`}
                                onClick={() => startRename(h)}
                                title="Rename this look"
                                aria-label="Rename this look"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white hover:bg-[#f3efe6] border border-[#eae4d9] text-[#64748b] transition"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              <button
                                type="button"
                                data-testid={`favorite-entry-${h.id}`}
                                onClick={() => onToggleFavorite(dog.id, h.id)}
                                title={h.favorite ? "Remove favourite" : "Mark as favourite"}
                                aria-label={h.favorite ? "Remove favourite" : "Mark as favourite"}
                                aria-pressed={!!h.favorite}
                                className={`inline-flex items-center justify-center w-9 h-9 rounded-full border transition ${
                                  h.favorite
                                    ? "bg-[#fff0ed] border-[#ff6b4a] text-[#ff6b4a]"
                                    : "bg-white hover:bg-[#f3efe6] border-[#eae4d9] text-[#64748b]"
                                }`}
                              >
                                <Heart className={`w-3.5 h-3.5 ${h.favorite ? "fill-[#ff6b4a]" : ""}`} />
                              </button>
                              <button
                                type="button"
                                data-testid={`share-entry-${h.id}`}
                                onClick={() => shareEntry(dog, h)}
                                disabled={sharing === h.id}
                                title={h.shareUrl ? "Copy share link" : "Create share link"}
                                aria-label="Share with groomer"
                                className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] border border-[#eae4d9] transition disabled:opacity-60"
                              >
                                {sharing === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                type="button"
                                data-testid={`reuse-style-${h.id}`}
                                onClick={() => navigate("/studio", { state: { dogId: dog.id, historyId: h.id } })}
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#ff6b4a] hover:bg-[#e85535] text-white text-xs font-semibold px-3.5 py-2 shadow-[0_6px_18px_-4px_rgba(255,107,74,0.4)] transition-all duration-200 active:scale-[0.98]"
                              >
                                <RotateCcw className="w-3.5 h-3.5" /> Reuse This Style
                              </button>
                            </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {dog.history?.length >= 2 && (
                      <button
                        type="button"
                        onClick={() => setCompareDog(dog)}
                        data-testid={`compare-looks-${dog.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#0f172a] hover:bg-[#1e293b] text-white text-xs font-semibold px-3.5 py-2 transition"
                      >
                        <GitCompare className="w-3.5 h-3.5" /> Compare two cuts
                      </button>
                    )}
                    <Link
                      to="/studio"
                      state={{ dogId: dog.id }}
                      data-testid={`use-style-again-${dog.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-[#f3efe6] text-[#0f172a] text-xs font-semibold px-3.5 py-2 border border-[#eae4d9] transition"
                    >
                      <Sparkles className="w-3.5 h-3.5" /> Try a new style
                    </Link>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <CompareModal dog={compareDog} onClose={() => setCompareDog(null)} />
    </div>
  );
}
